import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar: React.FC = () => {
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Sections from Sidebar for the Home dropdown
    const homeSections = [
        { name: 'Welcome', path: 'hero' },
        { name: 'Your Vision', path: 'vision' },
        { name: 'Process', path: 'process' },
        { name: 'Portfolio', path: 'gallery' },
        { name: 'Free Consultation', path: 'consultation' },
        { name: 'About', path: 'about' },
    ];

    const recommendations = [
        { name: 'Must Haves for Brides', path: '/recommendations/brides-top-products' },
    ];

    const handleMouseEnter = (menu: string) => setActiveDropdown(menu);
    const handleMouseLeave = () => setActiveDropdown(null);

    const scrollToSection = (sectionId: string) => {
        if (location.pathname !== '/') {
            // Navigate to home with hash
            navigate(`/#${sectionId}`);
        } else {
            // Already on home, just scroll
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
        setIsOpen(false);
        setActiveDropdown(null);
    };

    return (
        <nav className="bg-white/90 backdrop-blur-md shadow-md fixed w-full z-50 top-0 left-0 border-b border-warm-sand">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-20 items-center">
                    {/* Logo */}
                    <div className="flex-shrink-0 flex items-center">
                        <Link to="/" className="text-2xl font-serif text-deep-teal font-bold tracking-wide">
                            Kauai Wedding Stylist
                        </Link>
                    </div>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex space-x-8 items-center">
                        {/* Home Dropdown */}
                        <div
                            className="relative"
                            onMouseEnter={() => handleMouseEnter('home')}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                className={`text-gray-700 hover:text-island-sage px-3 py-2 rounded-md text-sm font-medium inline-flex items-center transition-colors ${location.pathname === '/' ? 'text-island-sage' : ''}`}
                            >
                                Home
                                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {activeDropdown === 'home' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute left-0 mt-0 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    >
                                        <div className="py-1">
                                            {homeSections.map((item) => (
                                                <button
                                                    key={item.path}
                                                    onClick={() => scrollToSection(item.path)}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-soft-cream hover:text-island-sage transition-colors"
                                                >
                                                    {item.name}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Recommendations Dropdown */}
                        <div
                            className="relative"
                            onMouseEnter={() => handleMouseEnter('recommendations')}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                className={`text-gray-700 hover:text-island-sage px-3 py-2 rounded-md text-sm font-medium inline-flex items-center transition-colors ${location.pathname.includes('recommendations') ? 'text-island-sage' : ''}`}
                            >
                                Recommendations
                                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {activeDropdown === 'recommendations' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute left-0 mt-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                                    >
                                        <div className="py-1">
                                            {recommendations.map((item) => (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-soft-cream hover:text-island-sage transition-colors"
                                                >
                                                    {item.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <Link
                            to="/contact"
                            className={`text-white bg-sunset-coral hover:bg-opacity-90 px-5 py-2 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg ${location.pathname === '/contact' ? 'bg-opacity-100' : ''}`}
                        >
                            Contact Us
                        </Link>
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="text-gray-700 hover:text-island-sage focus:outline-none p-2"
                        >
                            <span className="sr-only">Open main menu</span>
                            {!isOpen ? (
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            ) : (
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden bg-white border-t border-warm-sand overflow-hidden"
                    >
                        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                            {/* Home Mobile Link (Simple link for mobile for now to keep it clean, or could expand) */}
                            <div className="space-y-1 pl-4">
                                <p className="px-3 py-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
                                    Home
                                </p>
                                {homeSections.map((item) => (
                                    <button
                                        key={item.path}
                                        onClick={() => scrollToSection(item.path)}
                                        className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-island-sage hover:bg-soft-cream"
                                    >
                                        {item.name}
                                    </button>
                                ))}
                            </div>

                            <div className="space-y-1 pl-4">
                                <p className="px-3 py-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
                                    Recommendations
                                </p>
                                {recommendations.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        onClick={() => setIsOpen(false)}
                                        className="block px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-island-sage hover:bg-soft-cream"
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                            <Link
                                to="/contact"
                                onClick={() => setIsOpen(false)}
                                className="block px-3 py-2 rounded-md text-base font-medium text-sunset-coral hover:text-opacity-80 hover:bg-soft-cream"
                            >
                                Contact Us
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
